/**
 * Каталог образцов в панели «Отделка» — общий у стен и пола: вкладки видов со счётчиками,
 * разделы вкладки чипами («Детский принт», «Однотонные», «Мозаика»), поиск и бренд одной
 * строкой (бренд — выпадающим списком) и сетка карточек. Раздел и бренд — один фильтр
 * коллекции: у текстуры коллекция одна, поэтому выбор одного сбрасывает другое.
 * Что за вкладки и карточки и что делать по клику, решает панель стен или пола.
 *
 * Лёгкий при любой библиотеке: миниатюры по ~5 КБ грузятся лениво, а карточки
 * дорисовываются порциями (`FINISH_PAGE`) сами, когда до конца списка остаётся немного:
 * листаешь вниз — следующая порция уже на месте, без кнопки «Показать ещё».
 * Колонок образцов столько, сколько помещается в ширину панели (container queries).
 * Задержал мышь на образце-картинке — рядом крупный просмотр в настоящую величину (`FinishPeek`).
 * Разделы можно свернуть в одну строку кнопкой «−» — образцам больше места; выбор браузер
 * запоминает. Бренд — заметная кнопка-фильтр рядом с поиском: его раньше не находили.
 */
import { useRef, useState } from "react";
import { BASE_FINISH_COLLECTION } from "@/shared/catalog";
import type { PointerEvent } from "react";
import { UIKit } from "@/shared/ui";
import {
	FINISH_PAGE,
	collectionLabel,
	collectionsOf,
	matchesFinish,
	sortByCollection,
	type FinishCard,
} from "../logic";
import { FinishPeek, type FinishPeekTarget } from "./FinishPeek";

const ALL = "Все";
/** За сколько пикселей до конца списка дорисовывать следующую порцию */
const PRELOAD_PX = 800;
/** Где браузер помнит, свёрнуты ли разделы */
const FILTERS_COLLAPSED_KEY = "finish-filters-collapsed";

const readCollapsed = (): boolean => {
	try {
		return localStorage.getItem(FILTERS_COLLAPSED_KEY) === "1";
	} catch {
		return false;
	}
};

const saveCollapsed = (collapsed: boolean) => {
	try {
		localStorage.setItem(FILTERS_COLLAPSED_KEY, collapsed ? "1" : "0");
	} catch {
		// Хранилище недоступно (приватное окно) — просто не запомним
	}
};

/** Сколько держать мышь на образце, чтобы открылся крупный просмотр, мс */
const PEEK_DELAY_MS = 350;

export interface FinishBrowserTab<K> {
	key: K;
	label: string;
	cards: FinishCard[];
}

interface Props<K> {
	tabs: FinishBrowserTab<K>[];
	tab: K;
	onTab: (tab: K) => void;
	/** id карточки, что лежит сейчас — подсвечивается */
	activeId: string | null;
	/** Класть некуда (ничего не выделено) или только просмотр */
	disabled: boolean;
	onPick: (card: FinishCard) => void;
}

export const FinishBrowser = <K extends string | number>({
	tabs,
	tab,
	onTab,
	activeId,
	disabled,
	onPick,
}: Props<K>) => {
	const [collection, setCollection] = useState(ALL);
	const [search, setSearch] = useState("");
	const [filtersCollapsed, setFiltersCollapsed] = useState(readCollapsed);
	const toggleFilters = () => {
		setFiltersCollapsed(!filtersCollapsed);
		saveCollapsed(!filtersCollapsed);
	};

	const tabCards = sortByCollection(tabs.find((t) => t.key === tab)?.cards ?? []);
	const { sections, brands } = collectionsOf(tabCards);
	const activeCollection =
		sections.includes(collection) || brands.includes(collection) ? collection : ALL;
	const activeBrand = brands.includes(activeCollection) ? activeCollection : ALL;
	const cards = tabCards.filter(
		(card) =>
			(activeCollection === ALL ||
				(card.collection ?? BASE_FINISH_COLLECTION) === activeCollection) &&
			matchesFinish(card, search),
	);

	// Порция карточек сбрасывается, когда меняется сам список
	const listKey = `${tab}|${activeCollection}|${search}`;
	const [page, setPage] = useState({ key: listKey, count: FINISH_PAGE });
	const count = page.key === listKey ? page.count : FINISH_PAGE;
	const hasMore = cards.length > count;
	const scrollRef = useRef<HTMLDivElement>(null);

	// Крупный просмотр образца под мышью: открывается с задержкой, чтобы не мигать,
	// пока мышь просто едет по сетке
	const [peek, setPeek] = useState<FinishPeekTarget | null>(null);
	const peekTimer = useRef<number | undefined>(undefined);
	const startPeek = (card: FinishCard, event: PointerEvent<HTMLElement>) => {
		window.clearTimeout(peekTimer.current);
		const preview = card.preview;
		const panel = scrollRef.current;
		if (event.pointerType !== "mouse" || !preview || !panel) return;
		const top = event.currentTarget.getBoundingClientRect().top;
		const left = panel.getBoundingClientRect().right;
		peekTimer.current = window.setTimeout(
			() => setPeek({ card: { ...card, preview }, left, top }),
			PEEK_DELAY_MS,
		);
	};
	const stopPeek = () => {
		window.clearTimeout(peekTimer.current);
		setPeek(null);
	};

	// Новый список (вкладка, раздел, поиск) смотрят с начала
	const toTop = () => scrollRef.current?.scrollTo({ top: 0 });

	// Конец списка подъехал к краю — дорисовываем следующую порцию. Метка конца новая на
	// каждую порцию: если и её мало, чтобы заполнить панель, наблюдатель сработает снова
	const watchEnd = (sentinel: HTMLDivElement | null) => {
		const root = scrollRef.current;
		if (!sentinel || !root) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setPage({ key: listKey, count: count + FINISH_PAGE });
				}
			},
			{ root, rootMargin: `0px 0px ${PRELOAD_PX}px 0px` },
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
	};

	return (
		<>
			{/* Виды */}
			<div className="flex shrink-0 flex-wrap gap-1 border-b border-border px-2 py-1.5">
				{tabs.map((t) => (
					<button
						key={t.key}
						type="button"
						disabled={t.cards.length === 0}
						className={`flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 disabled:opacity-40 ${
							t.key === tab ? "bg-accent-soft text-ink" : "text-ink hover:bg-surface-hover"
						}`}
						onClick={() => {
							onTab(t.key);
							setCollection(ALL);
							toTop();
						}}
					>
						{t.label}
						<UIKit.Text as="span" className="text-xs font-normal text-muted">
							{t.cards.length}
						</UIKit.Text>
					</button>
				))}
			</div>

			{/* Разделы вкладки: по рисунку или назначению */}
			{sections.length > 1 && (
				<div className="flex shrink-0 items-start gap-1 border-b border-border px-2 py-1.5">
					<div
						className={`flex min-w-0 flex-1 flex-wrap gap-1 ${filtersCollapsed ? "max-h-6 overflow-hidden" : ""}`}
					>
						{[ALL, ...sections].map((name) => (
							<button
								key={name}
								type="button"
								aria-pressed={name === activeCollection}
								className={`rounded-full border px-2 py-0.5 text-xs transition-colors duration-150 ${
									name === activeCollection
										? "border-accent bg-accent-soft text-ink"
										: "border-border text-ink hover:bg-surface-hover"
								}`}
								onClick={() => {
									setCollection(name);
									toTop();
								}}
							>
								{name === ALL ? ALL : collectionLabel(name)}
							</button>
						))}
					</div>
					<button
						type="button"
						aria-expanded={!filtersCollapsed}
						title={filtersCollapsed ? "Показать все разделы" : "Свернуть разделы в одну строку"}
						className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border text-ink transition-colors duration-150 hover:bg-surface-hover"
						onClick={toggleFilters}
					>
						<svg viewBox="0 0 12 12" className="size-3" aria-hidden="true">
							<path
								d={filtersCollapsed ? "M2 6h8M6 2v8" : "M2 6h8"}
								stroke="currentColor"
								strokeWidth="1.6"
								strokeLinecap="round"
							/>
						</svg>
					</button>
				</div>
			)}

			{/* Поиск и бренд — одной строкой, чтобы образцам досталась вся ширина */}
			<div className="flex shrink-0 gap-1.5 border-b border-border px-3 py-2">
				<UIKit.SearchInput
					value={search}
					onChange={(value) => {
						setSearch(value);
						toTop();
					}}
					className="min-w-0 flex-1"
				/>
				{brands.length > 0 && (
					// Бренд — фильтр на полстроки с подписью и стрелкой, спокойный, как поиск.
					// Выбран бренд — синяя рамка и подложка, вместо стрелки крестик сброса
					<label
						title={activeBrand === ALL ? "Выбрать бренд" : activeBrand}
						className={`relative flex min-w-0 flex-1 cursor-pointer items-center rounded-md border transition-colors duration-150 ${
							activeBrand === ALL
								? "border-border bg-surface hover:border-accent/50"
								: "border-accent bg-accent-soft"
						}`}
					>
						<UIKit.Text
							as="span"
							className="pointer-events-none absolute left-2.5 text-xs font-semibold text-accent"
						>
							Бренд
						</UIKit.Text>
						<UIKit.Select
							bare
							aria-label="Бренд"
							value={activeBrand}
							options={[ALL, ...brands].map((name) => ({
								value: name,
								label: name === ALL ? "Все бренды" : collectionLabel(name),
							}))}
							onChange={(nextValue) => {
								setCollection(nextValue);
								toTop();
							}}
							className="w-full min-w-0 cursor-pointer appearance-none truncate bg-transparent py-1.5 pr-7 pl-14 text-xs font-medium text-ink focus:outline-none"
						/>
						{activeBrand === ALL ? (
							<svg
								viewBox="0 0 12 12"
								className="pointer-events-none absolute right-2.5 size-3 text-accent"
								aria-hidden="true"
							>
								<path
									d="M3 4.5 6 7.5 9 4.5"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.6"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						) : (
							<button
								type="button"
								aria-label="Все бренды"
								title="Сбросить бренд"
								className="absolute right-1 flex size-5 items-center justify-center rounded-full text-accent hover:bg-surface"
								onClick={(event) => {
									event.preventDefault();
									setCollection(ALL);
									toTop();
								}}
							>
								<svg viewBox="0 0 12 12" className="size-2.5" aria-hidden="true">
									<path
										d="M3 3l6 6M9 3l-6 6"
										stroke="currentColor"
										strokeWidth="1.6"
										strokeLinecap="round"
									/>
								</svg>
							</button>
						)}
					</label>
				)}
			</div>

			{/* Образцы */}
			<div
				ref={scrollRef}
				className="@container min-h-0 min-w-0 flex-1 overflow-y-auto p-2"
				// Список поехал — просмотр остался бы у старого места карточки
				onScroll={stopPeek}
			>
				{cards.length === 0 ? (
					<UIKit.Text className="px-3 py-8 text-center text-xs text-muted">
						Ничего не нашлось
					</UIKit.Text>
				) : (
					<>
						<div className="grid grid-cols-3 gap-2 @md:grid-cols-4 @xl:grid-cols-5">
							{cards.slice(0, count).map((card) => (
								// Обёртка ловит мышь и у неактивной карточки: смотреть образцы можно,
								// пока ничего не выделено
								<div
									key={card.id}
									className="flex min-w-0"
									onPointerEnter={(event) => startPeek(card, event)}
									onPointerLeave={stopPeek}
								>
									<FinishCardButton
										card={card}
										active={card.id === activeId}
										disabled={disabled}
										onPick={() => {
											stopPeek();
											onPick(card);
										}}
									/>
								</div>
							))}
						</div>
						{/* Метка конца списка: к ней подъехали — дорисовываем дальше */}
						{hasMore && <div key={count} ref={watchEnd} aria-hidden="true" className="h-px" />}
					</>
				)}
			</div>
			{peek && <FinishPeek target={peek} />}
		</>
	);
};

/** Карточка отделки: квадратный образец и название под ним. */
const FinishCardButton = ({
	card,
	active,
	disabled,
	onPick,
}: {
	card: FinishCard;
	active: boolean;
	disabled: boolean;
	onPick: () => void;
}) => (
	<button
		type="button"
		title={
			disabled
				? "Сначала выделите стену или комнату"
				: card.preview
					? undefined
					: `${card.name} · ${card.description}`
		}
		aria-pressed={active}
		disabled={disabled}
		className="group flex w-full min-w-0 flex-col gap-1 rounded-lg p-1 text-left transition-colors duration-150 hover:enabled:bg-surface-hover disabled:cursor-default"
		onClick={onPick}
	>
		<UIKit.Text
			as="span"
			className={`block aspect-square w-full overflow-hidden rounded-md shadow-sm ${
				active ? "ring-2 ring-accent" : "ring-1 ring-black/15"
			}`}
			style={card.style}
		>
			{card.thumb && (
				<img
					src={card.thumb}
					alt=""
					loading="lazy"
					decoding="async"
					draggable={false}
					className="size-full object-cover transition-transform duration-200 group-enabled:group-hover:scale-105"
				/>
			)}
		</UIKit.Text>
		<UIKit.Text
			as="span"
			className={`line-clamp-2 text-xs leading-tight ${active ? "font-semibold text-accent" : "text-ink"}`}
		>
			{card.name}
		</UIKit.Text>
	</button>
);
